import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import api from '../utils/api';

const Analytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('Last 1 month');

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await api.get('/admin/analytics');
        setData(res.data.data);
      } catch (err) {
        console.error('Analytics fetch error:', err);
        // No mock fallback in production — show real (empty) state instead of
        // fabricated numbers so the dashboard never misrepresents live data.
        setData({
          monthlySummary: { revenue: 0, newBusinesses: 0, newCustomers: 0 },
          customerGrowth: [],
          businessGrowth: [],
          dailyEngagement: [],
        });
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [range]);

  // Merge customer and business growth by month
  const mergedGrowth = (() => {
    if (!data) return [];
    const months = [
      ...new Set([
        ...(data.customerGrowth || []).map((d) => d.month),
        ...(data.businessGrowth || []).map((d) => d.month),
      ]),
    ];
    return months.map((month) => ({
      month,
      customers: data.customerGrowth?.find((d) => d.month === month)?.count || 0,
      businesses: data.businessGrowth?.find((d) => d.month === month)?.count || 0,
    }));
  })();

  if (loading) return <div className="page-loading">Loading analytics...</div>;

  const { monthlySummary, dailyEngagement } = data;

  return (
    <div className="page-wrap">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Analytics &amp; Reports</h1>
          <p className="page-subtitle">Track user activity, business metrics, and engagement</p>
        </div>
        <select
          className="range-select"
          value={range}
          onChange={(e) => setRange(e.target.value)}
          id="analytics-range-select"
        >
          <option>Last 1 month</option>
          <option>Last 3 months</option>
          <option>Last 6 months</option>
          <option>Last 1 year</option>
        </select>
      </div>

      {/* Monthly summary */}
      <div className="card mb-6">
        <h2 className="card-title">Monthly Summary Report</h2>
        <div className="summary-grid">
          <div className="summary-stat">
            <p className="summary-value">₹{(monthlySummary.revenue || 0).toLocaleString('en-IN')}</p>
            <p className="summary-label">Total Revenue This Month</p>
          </div>
          <div className="summary-stat">
            <p className="summary-value">{monthlySummary.newBusinesses}</p>
            <p className="summary-label">New Businesses Registered</p>
          </div>
          <div className="summary-stat">
            <p className="summary-value">{monthlySummary.newCustomers}</p>
            <p className="summary-label">New Customers Acquired</p>
          </div>
        </div>
      </div>

      {/* Charts row */}
      <div className="charts-grid">
        {/* User Growth */}
        <div className="card">
          <h2 className="card-title">User Growth</h2>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={mergedGrowth}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="customers"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ r: 4 }}
                name="Customers"
              />
              <Line
                type="monotone"
                dataKey="businesses"
                stroke="#22c55e"
                strokeWidth={2}
                dot={{ r: 4 }}
                name="Businesses"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Daily Engagement */}
        <div className="card">
          <h2 className="card-title">Daily Engagement</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dailyEngagement}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="customers" stackId="a" fill="#3b82f6" name="Customers" />
              <Bar dataKey="businessOwners" stackId="a" fill="#22c55e" name="Business Owners" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
